import { Button, Input, Pagination, Select, Space, Table, Tag, Tooltip, notification } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { paths } from 'routes/routes';
import {
  ACCOUNT_STATUS_OPTIONS,
  DEPARTMENT_LABEL,
  NO_GROUP_LABEL,
  STAR_GROUP_OPTIONS,
} from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { LedgerRow, StarAccountRow, StarAccountStatus, StarGroup } from 'pages/star-shared/types';
import { TABLE_EMPTY } from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import StarAmount from 'pages/star-shared/components/star-amount';
import { NoWrap, UserCell } from 'pages/star-shared/components/status-tags';
import AdjustModal from './components/adjust-modal';
import { MEMBER_ROLE_LABELS } from 'pages/user-manager/membership-labels';

const LIMIT = 20;
const NONE = 'NONE';

interface Filter {
  q?: string;
  // STAFF | CREATOR | NPC | NONE (chưa gán nhóm)
  group?: StarGroup | typeof NONE;
  page: number;
  limit: number;
}

const groupSelectOptions = [...STAR_GROUP_OPTIONS, { value: NONE, label: NO_GROUP_LABEL }];

function rolesOf(roles?: string | string[] | null): string[] {
  if (Array.isArray(roles)) return roles;
  if (typeof roles === 'string')
    return roles
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
  return [];
}

const StarAccountsPage = () => {
  const [filter, setFilter] = useState<Filter>({ page: 1, limit: LIMIT });
  const [refreshKey, setRefreshKey] = useState(0);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [adjustTarget, setAdjustTarget] = useState<StarAccountRow | null>(null);
  const { items, total, loading, setItems } = usePagedList<StarAccountRow>(
    API.starsAccounts,
    filter,
    {
      refreshKey,
    }
  );

  const patchAccount = async (
    row: StarAccountRow,
    patch: { starGroup?: StarGroup | null; status?: StarAccountStatus }
  ) => {
    setSavingId(row.userId);
    try {
      const res: any = await AxiosUtils.patch(`${API.starsAccounts}/${row.userId}`, patch);
      const updated = res?.data && typeof res.data === 'object' ? res.data : {};
      setItems((prev) =>
        prev.map((it) => (it.userId === row.userId ? { ...it, ...patch, ...updated } : it))
      );
      notification.success({ message: 'Đã cập nhật tài khoản Sao.' });
    } catch (err) {
      notification.error({ message: mapStarError(err) });
      setRefreshKey((k) => k + 1);
    } finally {
      setSavingId(null);
    }
  };

  const onAdjusted = (userId: number, row: LedgerRow | null) => {
    if (row && typeof row.balanceAfter === 'number') {
      setItems((prev) =>
        prev.map((it) => (it.userId === userId ? { ...it, balance: row.balanceAfter } : it))
      );
    } else {
      setRefreshKey((k) => k + 1);
    }
  };

  const columns: ColumnsType<StarAccountRow> = [
    {
      title: 'Người dùng',
      key: 'user',
      width: 240,
      render: (_, r) => (
        <UserCell
          user={{ id: r.userId, nickName: r.nickName, username: r.username, email: r.email }}
        />
      ),
    },
    {
      title: 'Bộ phận',
      key: 'department',
      width: 130,
      render: (_, r) => (
        <Space size={4} wrap>
          {r.department ? <Tag>{DEPARTMENT_LABEL[r.department] || r.department}</Tag> : '—'}
          {r.isHpCompany && <Tag color="blue">HP</Tag>}
        </Space>
      ),
    },
    {
      title: 'Vai trò',
      key: 'roles',
      width: 150,
      render: (_, r) => (
        <Space size={4} wrap>
          {rolesOf(r.roles).map((role) => (
            <Tag key={role}>{MEMBER_ROLE_LABELS[role] || role}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'TikTok UID',
      dataIndex: 'tiktokUid',
      key: 'tiktokUid',
      // UID TikTok 19 chữ số ≈ 180px ở Be Vietnam Pro 14px + 32px đệm ô.
      width: 215,
      render: (v) => <NoWrap>{v || '—'}</NoWrap>,
    },
    {
      title: 'Nhóm Sao',
      key: 'starGroup',
      width: 160,
      render: (_, r) => (
        <Select
          size="small"
          style={{ width: 140 }}
          value={r.starGroup || NONE}
          options={r.roles === 'CREATOR'
            ? [{ value: NONE, label: NO_GROUP_LABEL }]
            : r.roles === 'STUDIO_CREATOR'
              ? STAR_GROUP_OPTIONS.filter((item) => item.value === (r.studioCreatorBranch === 'NPC' ? 'NPC' : 'CREATOR'))
              : groupSelectOptions}
          loading={savingId === r.userId}
          disabled={savingId === r.userId}
          onChange={(value) =>
            patchAccount(r, { starGroup: value === NONE ? null : (value as StarGroup) })
          }
        />
      ),
    },
    {
      title: 'Số dư',
      key: 'balance',
      width: 120,
      align: 'right',
      render: (_, r) => (
        <Tooltip
          title={
            r.hasAccount === false
              ? 'Chưa có tài khoản Sao (tự tạo khi phát sinh giao dịch đầu tiên).'
              : undefined
          }
        >
          <span style={{ opacity: r.hasAccount === false ? 0.6 : 1 }}>
            <StarAmount value={r.balance} />
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 130,
      render: (_, r) => (
        <Select
          size="small"
          style={{ width: 110 }}
          value={r.status}
          options={ACCOUNT_STATUS_OPTIONS}
          loading={savingId === r.userId}
          disabled={savingId === r.userId}
          onChange={(value) => patchAccount(r, { status: value as StarAccountStatus })}
        />
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 190,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<PlusOutlined />} disabled={r.roles === 'CREATOR'} onClick={() => setAdjustTarget(r)}>
            Cộng/trừ Sao
          </Button>
          <Tooltip title="Xem lịch sử giao dịch">
            <Link
              to={`${paths.starLedger}?userId=${r.userId}&name=${encodeURIComponent(
                r.nickName || r.username || `#${r.userId}`
              )}`}
            >
              <Button size="small" type="text" icon={<UnorderedListOutlined />} />
            </Link>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageTitle title="Tài khoản Sao" />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Tìm theo tên, username, email, UID"
          style={{ width: 320 }}
          onSearch={(value) =>
            setFilter((prev) => ({ ...prev, q: value.trim() || undefined, page: 1 }))
          }
        />
        <Select
          allowClear
          placeholder="Nhóm Sao"
          style={{ width: 170 }}
          options={groupSelectOptions}
          value={filter.group}
          onChange={(value) =>
            setFilter((prev) => ({ ...prev, group: value as Filter['group'], page: 1 }))
          }
        />
      </div>
      <div className="custom-table">
        <Table
          columns={columns}
          rowKey="userId"
          pagination={false}
          dataSource={items}
          loading={loading}
          locale={TABLE_EMPTY}
          scroll={{ x: 1335 }}
        />
        {total > LIMIT && (
          <Pagination
            showSizeChanger={false}
            current={filter.page}
            onChange={(page) => setFilter((prev) => ({ ...prev, page }))}
            showLessItems
            total={total}
            pageSize={LIMIT}
          />
        )}
      </div>
      <AdjustModal
        account={adjustTarget}
        onClose={() => setAdjustTarget(null)}
        onSuccess={onAdjusted}
      />
    </div>
  );
};

export default StarAccountsPage;
